<script setup lang="ts">
import {VueFinalModal} from "vue-final-modal";

const {
  title,
  showCloseIcon = false,
  controlsClass = 'wad4-modal__controls'
} = defineProps<{
  title?: string;
  showCloseIcon?: boolean;
  controlsClass?: string;
}>();

const emit = defineEmits();
const slots = useSlots();

const options = ref({
  modelValue: false,
  teleportTo: 'body',
  displayDirective: 'if',
  hideOverlay: false,
  overlayTransition: 'vfm-fade',
  contentTransition: 'vfm-fade',
  clickToClose: showCloseIcon,
  escToClose: false,
  background: 'non-interactive',
  lockScroll: true,
  reserveScrollBarGap: true,
  swipeToClose: 'none',
  class: 'wad4-modal',
  contentClass: 'wad4-modal__content',
  zIndex: 1003
});

const closeModal = () => {
  options.value.modelValue = false;
  emit('update:modelValue', options.value.modelValue);
};

</script>

<template>
  <VueFinalModal v-bind="options" :zIndexFn="() => options.zIndex">
    <div class="wad4-modal__content-wrapper">
      <header v-if="title || slots.title || showCloseIcon" class="wad4-modal__header">
        <button v-if="showCloseIcon" class="wad4-button-ghost wad4-modal__close" @pointerup="closeModal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
          </svg>
        </button>
        <slot name="title">
          <h1 v-if="title" class="wad4-modal__title">{{ title }}</h1>
        </slot>
      </header>
      <main class="wad4-modal__body">
        <slot />
      </main>
      <footer v-if="slots.controls && slots.controls().length > 0" :class="controlsClass">
        <slot name="controls"/>
      </footer>
    </div>
  </VueFinalModal>
</template>

<style lang="scss">
.wad4-modal {
  display: flex;
  justify-content: center;
  align-items: center;

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    border-radius: $wad4-border-radius-lg;
    background-color: $wad4-c-background-bg-primary;
    width: 100%;
    max-height: 95vh;
    padding: $wad4-spacing-3xl $wad4-container-spacing;
    gap: $wad4-spacing-3xl;

    @include mqBreakPoint(0, $wad4-breakpoint-tablet-max) {
      align-self: flex-end;
      border-radius: $wad4-border-radius-lg $wad4-border-radius-lg 0 0;
    }
    @include mqBreakPoint($wad4-breakpoint-tablet-min, $wad4-breakpoint-tablet-max) {
      padding: $wad4-spacing-5xl;
    }
    @include mqBreakPoint($wad4-breakpoint-desktop-min) {
      width: 460px;
      padding: $wad4-spacing-5xl;

      &--narrow {
        width: 344px;
      }

      &--large {
        width: 700px;
      }
    }

    &-wrapper {
      width: 100%;
      margin-right: -$wad4-spacing-md;
      padding-right: $wad4-spacing-md;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: $wad4-spacing-3xl;

      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-track {
        background: $wad4-c-background-bg-brand-tertiary;
        border-radius: 10px;
      }

      &::-webkit-scrollbar-thumb {
        background: $wad4-c-foreground-fg-brand-solid;
        border-radius: 10px;
      }
    }
  }

  &__header {
    display: flex;
    align-items: flex-start;
  }

  &__close {
    position: absolute;
    right: 20px;
    top: 20px;
    color: $wad4-c-text-text-secondary;
    cursor: pointer;
  }

  &__title {
    margin: 0;
    @include display-size-mixin('xxs');
    font-family: $wad4-default-font-unbounded;
    color: $wad4-c-text-text-primary;
    font-weight: $wad4-font-weight-regular-bold;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-3xl;
    color: $wad4-c-text-text-primary;
  }

  &__controls {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: $wad4-spacing-lg;
    align-self: stretch;

    @include mqBreakPoint($wad4-breakpoint-tablet-min, $wad4-breakpoint-tablet-max) {
      flex-direction: row;
      align-items: flex-start;
    }

    & > button {
      flex: 1;
      white-space: nowrap;
    }
  }

  & .vfm--overlay {
    background-color: rgba(37, 44, 62, 0.76);
  }
}
</style>
